// T034: Deck Validator

import type { CardDefinition, Faction } from '../../types';

const DECK_SIZE = 20;
const MAX_MANA_COST = 15;
const MAX_OVERLOAD_COPIES = 3;

// Check if a faction is neutral
function isNeutralFaction(faction: Faction): boolean {
  return faction === 'neutral_spell' || faction === 'neutral_unit';
}

export type DeckValidationErrorCode =
  | 'INVALID_DECK_SIZE'
  | 'DUPLICATE_CARDS'
  | 'OVERLOAD_EXCEEDED'
  | 'INVALID_FACTION'
  | 'NO_FACTION_CARDS'
  | 'MANA_LIMIT_EXCEEDED';

export interface DeckStats {
  totalCards: number;
  factionCards: number;
  neutralCards: number;
  totalManaCost: number;
  unitCount: number;
  spellCount: number;
}

export interface DeckValidationResult {
  isValid: boolean;
  errors: DeckValidationErrorCode[];
  stats?: DeckStats;
  duplicateCards?: string[];
  invalidFactionCards?: string[];
}

export type CanAddReason =
  | 'DECK_FULL'
  | 'DUPLICATE'
  | 'OVERLOAD_MAX'
  | 'WRONG_FACTION'
  | 'MANA_EXCEEDED';

export interface CanAddResult {
  canAdd: boolean;
  reason?: CanAddReason;
}

/**
 * Check if a card has the Overload effect.
 */
function hasOverload(card: CardDefinition): boolean {
  return card.effectIds?.includes('overload') ?? false;
}

/**
 * Check if a card is a magic card (spell or field).
 */
function isMagicCard(card: CardDefinition): boolean {
  return card.type === 'spell' || card.type === 'field';
}

/**
 * Get the mana cost of a card (0 for units).
 */
function getManaCost(card: CardDefinition): number {
  if (!isMagicCard(card)) return 0;
  return card.cost ?? 0;
}

/**
 * Calculate deck statistics.
 */
function calculateStats(deck: CardDefinition[], deckFaction: Faction): DeckStats {
  let factionCards = 0;
  let neutralCards = 0;
  let totalManaCost = 0;
  let unitCount = 0;
  let spellCount = 0;

  for (const card of deck) {
    if (card.faction === deckFaction) {
      factionCards++;
    } else if (isNeutralFaction(card.faction)) {
      neutralCards++;
    }

    totalManaCost += getManaCost(card);

    if (card.type === 'unit') {
      unitCount++;
    } else {
      spellCount++;
    }
  }

  return {
    totalCards: deck.length,
    factionCards,
    neutralCards,
    totalManaCost,
    unitCount,
    spellCount,
  };
}

/**
 * Validate deck size (must be exactly 20 cards).
 */
function validateDeckSize(deck: CardDefinition[]): DeckValidationErrorCode | null {
  if (deck.length !== DECK_SIZE) {
    return 'INVALID_DECK_SIZE';
  }
  return null;
}

/**
 * Validate no duplicate cards (except Overload cards up to 3).
 */
function validateDuplicates(
  deck: CardDefinition[]
): { error: DeckValidationErrorCode | null; duplicates: string[] } {
  const cardCounts = new Map<string, { count: number; hasOverload: boolean }>();
  const duplicates: string[] = [];

  for (const card of deck) {
    const existing = cardCounts.get(card.id);
    if (existing) {
      existing.count++;
    } else {
      cardCounts.set(card.id, { count: 1, hasOverload: hasOverload(card) });
    }
  }

  let hasDuplicateError = false;
  let hasOverloadExceeded = false;

  for (const [cardId, info] of cardCounts) {
    if (info.count > 1) {
      if (info.hasOverload) {
        if (info.count > MAX_OVERLOAD_COPIES) {
          hasOverloadExceeded = true;
          duplicates.push(cardId);
        }
      } else {
        hasDuplicateError = true;
        duplicates.push(cardId);
      }
    }
  }

  if (hasOverloadExceeded) {
    return { error: 'OVERLOAD_EXCEEDED', duplicates };
  }
  if (hasDuplicateError) {
    return { error: 'DUPLICATE_CARDS', duplicates };
  }

  return { error: null, duplicates: [] };
}

/**
 * Validate faction restrictions.
 */
function validateFaction(
  deck: CardDefinition[],
  deckFaction: Faction
): { error: DeckValidationErrorCode | null; invalidCards: string[] } {
  const invalidCards: string[] = [];
  let factionCardCount = 0;

  for (const card of deck) {
    if (card.faction === deckFaction) {
      factionCardCount++;
    } else if (!isNeutralFaction(card.faction)) {
      invalidCards.push(card.id);
    }
  }

  if (invalidCards.length > 0) {
    return { error: 'INVALID_FACTION', invalidCards };
  }

  if (factionCardCount === 0) {
    return { error: 'NO_FACTION_CARDS', invalidCards: [] };
  }

  return { error: null, invalidCards: [] };
}

/**
 * Validate magic card mana limit.
 */
function validateManaCost(deck: CardDefinition[]): DeckValidationErrorCode | null {
  let totalMana = 0;

  for (const card of deck) {
    totalMana += getManaCost(card);
  }

  if (totalMana > MAX_MANA_COST) {
    return 'MANA_LIMIT_EXCEEDED';
  }

  return null;
}

/**
 * Validate a complete deck.
 */
export function validate(deck: CardDefinition[], deckFaction: Faction): DeckValidationResult {
  const errors: DeckValidationErrorCode[] = [];
  const duplicateCards: string[] = [];
  const invalidFactionCards: string[] = [];

  // Validate deck size
  const sizeError = validateDeckSize(deck);
  if (sizeError) {
    errors.push(sizeError);
  }

  // Validate duplicates
  const duplicateResult = validateDuplicates(deck);
  if (duplicateResult.error) {
    errors.push(duplicateResult.error);
    duplicateCards.push(...duplicateResult.duplicates);
  }

  // Validate faction
  const factionResult = validateFaction(deck, deckFaction);
  if (factionResult.error) {
    errors.push(factionResult.error);
    invalidFactionCards.push(...factionResult.invalidCards);
  }

  // Validate mana cost
  const manaError = validateManaCost(deck);
  if (manaError) {
    errors.push(manaError);
  }

  const isValid = errors.length === 0;
  const stats = calculateStats(deck, deckFaction);

  return {
    isValid,
    errors,
    stats,
    duplicateCards: duplicateCards.length > 0 ? duplicateCards : undefined,
    invalidFactionCards: invalidFactionCards.length > 0 ? invalidFactionCards : undefined,
  };
}

/**
 * Check if a card can be added to a deck.
 */
export function canAddCard(
  deck: CardDefinition[],
  card: CardDefinition,
  deckFaction: Faction
): CanAddResult {
  // Check deck size
  if (deck.length >= DECK_SIZE) {
    return { canAdd: false, reason: 'DECK_FULL' };
  }

  // Check faction
  if (card.faction !== deckFaction && !isNeutralFaction(card.faction)) {
    return { canAdd: false, reason: 'WRONG_FACTION' };
  }

  // Check duplicates
  const existingCount = deck.filter((c) => c.id === card.id).length;
  if (existingCount > 0) {
    if (hasOverload(card)) {
      if (existingCount >= MAX_OVERLOAD_COPIES) {
        return { canAdd: false, reason: 'OVERLOAD_MAX' };
      }
    } else {
      return { canAdd: false, reason: 'DUPLICATE' };
    }
  }

  // Check mana cost
  if (isMagicCard(card)) {
    let currentMana = 0;
    for (const c of deck) {
      currentMana += getManaCost(c);
    }
    if (currentMana + getManaCost(card) > MAX_MANA_COST) {
      return { canAdd: false, reason: 'MANA_EXCEEDED' };
    }
  }

  return { canAdd: true };
}

/**
 * Get remaining mana budget for magic cards.
 */
export function getRemainingMana(deck: CardDefinition[]): number {
  let usedMana = 0;
  for (const card of deck) {
    usedMana += getManaCost(card);
  }
  return Math.max(0, MAX_MANA_COST - usedMana);
}

/**
 * Get the count of a specific card in the deck.
 */
export function getCardCount(deck: CardDefinition[], cardId: string): number {
  return deck.filter((c) => c.id === cardId).length;
}

// ============================================================
// Competition Mode Validation
// ============================================================

const COMPETITION_POOL_SIZE = 35;
const COMPETITION_FOREST_SIZE = 15;
const MAX_GOD_CARDS = 1;
const MAX_TRAVELER_CARDS = 10;

export type CompetitionErrorCode =
  | DeckValidationErrorCode
  | 'INVALID_POOL_SIZE'
  | 'GOD_RACE_EXCEEDED'
  | 'TRAVELER_EXCEEDED';

export interface CompetitionValidationResult {
  isValid: boolean;
  errors: CompetitionErrorCode[];
  deckCards?: CardDefinition[];
  forestCards?: CardDefinition[];
}

/**
 * Check if a card has the God race (神族) specialType.
 */
function isGodRace(card: CardDefinition): boolean {
  // Check effectIds for 'god_race' or check effectText for 神族
  return card.effectText?.includes('神族') ?? false;
}

/**
 * Check if a card belongs to the traveler faction.
 */
function isTravelerFaction(card: CardDefinition): boolean {
  return card.faction === 'traveler';
}

/**
 * Validate a competition deck (35-card pool: 20 deck + 15 forest).
 */
export function validateCompetition(
  pool: CardDefinition[],
  deckFaction: Faction
): CompetitionValidationResult {
  const errors: CompetitionErrorCode[] = [];

  // Pool must be exactly 35 cards
  if (pool.length !== COMPETITION_POOL_SIZE) {
    errors.push('INVALID_POOL_SIZE');
    return { isValid: false, errors };
  }

  // Split: first 20 = deck, remaining 15 = forest
  const deckCards = pool.slice(0, DECK_SIZE);
  const forestCards = pool.slice(DECK_SIZE, DECK_SIZE + COMPETITION_FOREST_SIZE);

  // Validate the deck portion with standard rules
  const deckResult = validate(deckCards, deckFaction);
  errors.push(...deckResult.errors);

  // Competition restrictions on the full pool
  let godCount = 0;
  let travelerCount = 0;

  for (const card of pool) {
    if (isGodRace(card)) {
      godCount++;
    }
    if (isTravelerFaction(card)) {
      travelerCount++;
    }
  }

  if (godCount > MAX_GOD_CARDS) {
    errors.push('GOD_RACE_EXCEEDED');
  }

  if (travelerCount > MAX_TRAVELER_CARDS) {
    errors.push('TRAVELER_EXCEEDED');
  }

  // Check duplicates across the full pool
  const poolCounts = new Map<string, { count: number; hasOverload: boolean }>();
  for (const card of pool) {
    const existing = poolCounts.get(card.id);
    if (existing) {
      existing.count++;
    } else {
      poolCounts.set(card.id, { count: 1, hasOverload: hasOverload(card) });
    }
  }

  for (const [, info] of poolCounts) {
    if (info.count > 1) {
      if (info.hasOverload) {
        if (info.count > MAX_OVERLOAD_COPIES) {
          if (!errors.includes('OVERLOAD_EXCEEDED')) {
            errors.push('OVERLOAD_EXCEEDED');
          }
        }
      } else {
        if (!errors.includes('DUPLICATE_CARDS')) {
          errors.push('DUPLICATE_CARDS');
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    deckCards,
    forestCards,
  };
}

// Re-export as object for backwards compatibility
export const DeckValidator = {
  validate,
  validateCompetition,
  canAddCard,
  getRemainingMana,
  getCardCount,
};
